import { useState, useEffect } from "react";
import {
  Check, Globe, Eye, EyeOff, Copy, LogOut,
  Palette, Mail, Server, User, ChevronRight,
  Sun, Moon, Monitor, ArrowLeft
} from "lucide-react";
import { motion, AnimatePresence } from "motion/react";
import cloudflareApi from "../lib/cloudflare-api";
import { getCloudflareConfig, saveCloudflareConfig } from "../lib/storage";
import { Button } from "./ui";

interface SettingsModalProps {
  onClose: () => void;
  theme: 'light' | 'dark' | 'auto';
  onThemeChange: (theme: 'light' | 'dark' | 'auto') => void;
  onLogout?: () => void;
}

interface MailgunConfig {
  api_key: string;
  domain: string;
  from_email: string;
  from_name: string;
  signature: string;
  webhook_signing_key: string;
}

interface CloudflareConfig {
  worker_url: string;
  user_email: string;
  api_key: string;
  registered: boolean;
}

interface UserStats {
  email_count: number;
}

type SectionId = 'appearance' | 'mailgun' | 'cloudflare' | 'account';

interface Section {
  id: SectionId;
  label: string;
  icon: React.ReactNode;
  description: string;
}

const sections: Section[] = [
  { id: 'appearance', label: 'Apariencia', icon: <Palette size={18} />, description: 'Tema y personalización' },
  { id: 'mailgun', label: 'Mailgun', icon: <Mail size={18} />, description: 'Envío de correos' },
  { id: 'cloudflare', label: 'Cloudflare', icon: <Server size={18} />, description: 'Recepción y almacenamiento' },
  { id: 'account', label: 'Cuenta', icon: <User size={18} />, description: 'Sesión y datos' },
];

export default function SettingsModal({ onClose, theme, onThemeChange, onLogout }: SettingsModalProps) {
  const [config, setConfig] = useState<MailgunConfig>({
    api_key: "",
    domain: "",
    from_email: "",
    from_name: "Mocaccino",
    signature: "",
    webhook_signing_key: "",
  });
  const [showApiKey, setShowApiKey] = useState(false);
  const [cloudflareConfig, setCloudflareConfig] = useState<CloudflareConfig>({
    worker_url: "",
    user_email: "",
    api_key: "",
    registered: false,
  });
  const [userStats, setUserStats] = useState<UserStats | null>(null);
  const [expandedSections, setExpandedSections] = useState<Record<SectionId, boolean>>({
    appearance: true,
    mailgun: false,
    cloudflare: false,
    account: false,
  });
  const [savingSection, setSavingSection] = useState<string | null>(null);
  const [savedSection, setSavedSection] = useState<string | null>(null);

  useEffect(() => {
    loadConfig();
    loadCloudflareConfig();
  }, []);

  const loadConfig = async () => {
    try {
      const result = await cloudflareApi.getMe();
      if (result.mailgun_config) {
        setConfig({
          api_key: result.mailgun_config.api_key || "",
          domain: result.mailgun_config.domain || "",
          from_email: result.mailgun_config.from_email || "",
          from_name: result.mailgun_config.from_name || "Mocaccino",
          signature: result.mailgun_config.signature || "",
          webhook_signing_key: result.mailgun_config.webhook_signing_key || "",
        });
      }
      setUserStats(result.stats);
    } catch (error) {
      console.error("Error loading config:", error);
    }
  };

  const loadCloudflareConfig = () => {
    try {
      const cfg = getCloudflareConfig();
      setCloudflareConfig({
        worker_url: cfg.worker_url,
        user_email: cfg.user_email,
        api_key: "",
        registered: true,
      });
    } catch (error) {
      console.error("Error loading Cloudflare config:", error);
    }
  };

  const handleSaveMailgun = async () => {
    setSavingSection('mailgun');
    try {
      await cloudflareApi.updateMailgunConfig({
        api_key: config.api_key,
        domain: config.domain,
        from_email: config.from_email,
        from_name: config.from_name || undefined,
        signature: config.signature || undefined,
        webhook_signing_key: config.webhook_signing_key || undefined,
      });
      setSavedSection('mailgun');
      setTimeout(() => setSavedSection(null), 2500);
    } catch (error) {
      console.error("Error saving config:", error);
      alert("Error al guardar la configuración");
    } finally {
      setSavingSection(null);
    }
  };

  const handleSaveAppearance = async () => {
    setSavingSection('appearance');
    try {
      await cloudflareApi.updateMailgunConfig({
        api_key: config.api_key,
        domain: config.domain,
        from_email: config.from_email,
        from_name: config.from_name || undefined,
        signature: config.signature || undefined,
        webhook_signing_key: config.webhook_signing_key || undefined,
      });
      setSavedSection('appearance');
      setTimeout(() => setSavedSection(null), 2500);
    } catch (error) {
      console.error("Error saving config:", error);
    } finally {
      setSavingSection(null);
    }
  };

  const handleSaveCloudflareConfig = () => {
    setSavingSection('cloudflare');
    try {
      saveCloudflareConfig({
        worker_url: cloudflareConfig.worker_url,
        user_email: cloudflareConfig.user_email,
      });
      setSavedSection('cloudflare');
      setTimeout(() => setSavedSection(null), 2500);
    } catch (error) {
      console.error("Error saving Cloudflare config:", error);
      alert("Error al guardar configuración");
    } finally {
      setSavingSection(null);
    }
  };

  const copyToClipboard = async (text: string, label: string) => {
    try {
      await navigator.clipboard.writeText(text);
      alert(`✅ ${label} copiado al portapapeles`);
    } catch (error) {
      console.error("Error copying to clipboard:", error);
      alert("❌ Error al copiar");
    }
  };

  const toggleSection = (id: SectionId) => {
    setExpandedSections(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const SaveButton = ({ section, onClick }: { section: string; onClick: () => void }) => (
    <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 16 }}>
      <Button
        variant="primary"
        size="sm"
        onClick={onClick}
        icon={savingSection === section ? undefined : (savedSection === section ? <Check size={14} /> : undefined)}
      >
        {savingSection === section ? "Guardando..." : savedSection === section ? "Guardado" : "Guardar"}
      </Button>
    </div>
  );

  const renderSectionContent = (id: SectionId) => {
    switch (id) {
      case 'appearance':
        return (
          <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
            <div className="settings-field">
              <label>Tema de la interfaz</label>
              <div className="theme-selector">
                {[
                  { value: 'light', label: 'Claro', icon: <Sun size={20} /> },
                  { value: 'dark', label: 'Oscuro', icon: <Moon size={20} /> },
                  { value: 'auto', label: 'Sistema', icon: <Monitor size={20} /> },
                ].map((option) => (
                  <button
                    key={option.value}
                    className={`theme-option ${theme === option.value ? 'active' : ''}`}
                    onClick={() => onThemeChange(option.value as 'light' | 'dark' | 'auto')}
                    style={{ transition: "all 0.15s ease" }}
                  >
                    <span className="theme-emoji">{option.icon}</span>
                    <span className="theme-label">{option.label}</span>
                  </button>
                ))}
              </div>
              <small>El modo sistema detecta automáticamente la preferencia de tu dispositivo</small>
            </div>

            <div className="settings-field">
              <label>Firma de correo</label>
              <textarea
                placeholder="Saludos,&#10;Tu nombre&#10;Tu cargo"
                value={config.signature}
                onChange={(e) => setConfig({ ...config, signature: e.target.value })}
                rows={4}
                className="settings-textarea"
              />
              <small>Se agregará automáticamente al final de tus correos</small>
            </div>

            <SaveButton section="appearance" onClick={handleSaveAppearance} />
          </div>
        );

      case 'mailgun':
        return (
          <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
            <div className="settings-grid">
              <div className="settings-field">
                <label>Private API Key</label>
                <div className="input-with-actions">
                  <input
                    type={showApiKey ? "text" : "password"}
                    placeholder="key-xxxxxxxxxxxxxxxx"
                    value={config.api_key}
                    onChange={(e) => setConfig({ ...config, api_key: e.target.value })}
                  />
                  <button
                    type="button"
                    className="input-action-button"
                    onClick={() => setShowApiKey(!showApiKey)}
                    title={showApiKey ? "Ocultar" : "Mostrar"}
                  >
                    {showApiKey ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                  {config.api_key && (
                    <button
                      type="button"
                      className="input-action-button"
                      onClick={() => copyToClipboard(config.api_key, "API Key")}
                      title="Copiar"
                    >
                      <Copy size={16} />
                    </button>
                  )}
                </div>
                <small>Send → API keys → Private key</small>
              </div>

              <div className="settings-field">
                <label>Dominio</label>
                <input
                  type="text"
                  placeholder="tudominio.com"
                  value={config.domain}
                  onChange={(e) => setConfig({ ...config, domain: e.target.value })}
                />
                <small>Send → Domain settings</small>
              </div>

              <div className="settings-field">
                <label>Email remitente</label>
                <input
                  type="email"
                  placeholder="correo@tudominio.com"
                  value={config.from_email}
                  onChange={(e) => setConfig({ ...config, from_email: e.target.value })}
                />
                <small>Dirección desde la que enviarás correos</small>
              </div>

              <div className="settings-field">
                <label>Nombre remitente</label>
                <input
                  type="text"
                  placeholder="Tu Nombre"
                  value={config.from_name}
                  onChange={(e) => setConfig({ ...config, from_name: e.target.value })}
                />
                <small>Nombre que verán los destinatarios</small>
              </div>
            </div>

            <div className="settings-field">
              <label>Webhook Signing Key <span className="optional-badge">Opcional</span></label>
              <div className="input-with-actions">
                <input
                  type="password"
                  placeholder="key-xxxxxxxxxxxxxxxx"
                  value={config.webhook_signing_key}
                  onChange={(e) => setConfig({ ...config, webhook_signing_key: e.target.value })}
                />
                {config.webhook_signing_key && (
                  <button
                    type="button"
                    className="input-action-button"
                    onClick={() => copyToClipboard(config.webhook_signing_key, "Webhook Key")}
                    title="Copiar"
                  >
                    <Copy size={16} />
                  </button>
                )}
              </div>
              <small>Sending → Webhooks → Webhook signing key. Verifica la autenticidad de correos entrantes.</small>
            </div>

            <SaveButton section="mailgun" onClick={handleSaveMailgun} />
          </div>
        );

      case 'cloudflare':
        return (
          <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
            {userStats && (
              <div className="settings-status-banner success">
                <Check size={18} />
                <div>
                  <strong>Conectado</strong>
                  <span>{cloudflareConfig.user_email} • {userStats.email_count || 0} correos</span>
                </div>
              </div>
            )}

            <div className="settings-grid">
              <div className="settings-field">
                <label>URL del Worker</label>
                <input
                  type="text"
                  placeholder="https://tu-worker.tu-usuario.workers.dev"
                  value={cloudflareConfig.worker_url}
                  onChange={(e) => setCloudflareConfig({ ...cloudflareConfig, worker_url: e.target.value })}
                />
                <small>La URL de tu Worker desplegado en Cloudflare</small>
              </div>

              <div className="settings-field">
                <label>Tu email</label>
                <input
                  type="email"
                  placeholder="tu@tudominio.com"
                  value={cloudflareConfig.user_email}
                  onChange={(e) => setCloudflareConfig({ ...cloudflareConfig, user_email: e.target.value })}
                />
                <small>Email con el que recibirás correos</small>
              </div>
            </div>

            <div className="settings-info-box">
              <Globe size={16} />
              <p>Los correos se sincronizan automáticamente con Cloudflare, permitiendo acceso desde múltiples dispositivos.</p>
            </div>

            <SaveButton section="cloudflare" onClick={handleSaveCloudflareConfig} />
          </div>
        );

      case 'account':
        return (
          <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
            <div className="account-info">
              <div className="account-avatar">
                {cloudflareConfig.user_email.substring(0, 2).toUpperCase()}
              </div>
              <div className="account-details">
                <strong>{cloudflareConfig.user_email.split('@')[0]}</strong>
                <span>{cloudflareConfig.user_email}</span>
              </div>
            </div>

            <div style={{
              borderTop: "1px solid var(--border)",
              paddingTop: 16,
              marginTop: 4,
            }}>
              <h4 style={{
                fontSize: 14,
                fontWeight: 600,
                color: "#f43f5e",
                margin: "0 0 8px 0",
              }}>Zona de peligro</h4>
              <p style={{
                fontSize: 13,
                color: "var(--text-tertiary)",
                margin: "0 0 12px 0",
              }}>Estas acciones pueden afectar tu acceso a la aplicación</p>
              <Button
                variant="danger"
                icon={<LogOut size={16} />}
                onClick={async () => {
                  if (confirm("¿Estás seguro que deseas cerrar sesión?")) {
                    try {
                      await cloudflareApi.logout();
                    } catch (error) {
                      console.error("Error logging out:", error);
                    } finally {
                      onLogout?.();
                    }
                  }
                }}
              >
                Cerrar sesión
              </Button>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="settings-page">
      {/* Page header */}
      <div className="settings-page-header">
        <button
          onClick={onClose}
          className="settings-back-btn"
        >
          <ArrowLeft size={18} />
          <span>Volver</span>
        </button>
        <h1>Configuración</h1>
      </div>

      {/* Scrollable content */}
      <div className="settings-page-scroll">
        <div className="settings-page-content">
          {sections.map((section) => {
            const isExpanded = expandedSections[section.id];
            return (
              <div key={section.id} className="settings-section-card">
                <button
                  className="settings-section-toggle"
                  onClick={() => toggleSection(section.id)}
                >
                  <span className="settings-section-icon">{section.icon}</span>
                  <div className="settings-section-info">
                    <span className="settings-section-label">{section.label}</span>
                    <span className="settings-section-desc">{section.description}</span>
                  </div>
                  <motion.span
                    animate={{ rotate: isExpanded ? 90 : 0 }}
                    transition={{ duration: 0.15 }}
                    style={{ display: "flex", color: "var(--text-tertiary)" }}
                  >
                    <ChevronRight size={16} />
                  </motion.span>
                </button>
                <AnimatePresence initial={false}>
                  {isExpanded && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.2, ease: [0.16, 1, 0.3, 1] }}
                      style={{ overflow: "hidden" }}
                    >
                      <div className="settings-section-body">
                        {renderSectionContent(section.id)}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
